import { isSameMonth, isToday } from "date-fns";

import type { ShippingCalendarEntry } from "./api";
import DayCell from "./DayCell";
import {
  dateKey,
  parseMonthKey,
  WEEK_STARTS_ON,
  WEEKDAY_LABELS,
} from "./calendarDates";

/**
 * 月のグリッド（曜日の見出し ＋ 7 × 5〜6 のマス）。
 *
 * 行数は月によって変わるので `gridTemplateRows` はインラインで作る（Tailwind の
 * クラス名は実行時の値を取れない）。高さは親から受けた枠を等分し、**画面に
 * 収める**。ページを縦スクロールさせると、月全体を一目で見るという目的が
 * 崩れるため。
 */
export default function MonthGrid({
  month,
  days,
  entriesByDate,
  today,
  editable,
  dropTarget,
  movingKeys,
  onDragStart,
  onDragEnd,
  onDropOn,
  onDragOver,
  onDragLeave,
  onMoveByDays,
  onEditDate,
}: {
  /** 表示している月（`2026-10`）。前月末・翌月頭のマスを見分けるのに使う。 */
  month: string;
  days: Date[];
  entriesByDate: Map<string, ShippingCalendarEntry[]>;
  /** 「今日」の 0 時。過去の日（遅れ）の判定に使う。 */
  today: Date;
  editable: boolean;
  dropTarget: string | null;
  movingKeys: Set<string>;
  onDragStart: (entry: ShippingCalendarEntry) => void;
  onDragEnd: () => void;
  onDropOn: (date: string) => void;
  onDragOver: (date: string) => void;
  onDragLeave: (date: string) => void;
  onMoveByDays: (entry: ShippingCalendarEntry, days: number) => void;
  onEditDate: (entry: ShippingCalendarEntry) => void;
}) {
  // 前月末・翌月頭のマスを薄く出すための基準。
  const monthStart = parseMonthKey(month);
  const rows = Math.ceil(days.length / 7);

  return (
    <div className="flex min-h-0 flex-1 flex-col border-t border-default-200">
      <div className="grid shrink-0 grid-cols-7">
        {WEEKDAY_LABELS.map((label, index) => {
          // 0=日曜 … 6=土曜。並びを変えても色が付いて回るように番号で見る。
          const weekday = (WEEK_STARTS_ON + index) % 7;
          return (
            <div
              key={label}
              className={[
                "border-b border-r border-default-200 py-1 text-center text-xs font-bold",
                weekday === 0 ? "text-danger" : "",
                weekday === 6 ? "text-brand-400" : "",
                weekday !== 0 && weekday !== 6 ? "text-default-600" : "",
              ].join(" ")}
            >
              {label}
            </div>
          );
        })}
      </div>
      <div
        className="grid min-h-0 flex-1 grid-cols-7 border-l border-default-200"
        style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}
      >
        {days.map((day) => {
          const key = dateKey(day);
          return (
            <DayCell
              key={key}
              date={day}
              entries={entriesByDate.get(key) ?? []}
              inMonth={monthStart ? isSameMonth(day, monthStart) : true}
              isToday={isToday(day)}
              isPast={day < today}
              editable={editable}
              isDropTarget={dropTarget === key}
              movingKeys={movingKeys}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDropOn={onDropOn}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onMoveByDays={onMoveByDays}
              onEditDate={onEditDate}
            />
          );
        })}
      </div>
    </div>
  );
}
